import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack, KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';
import { formatDate as fmtDate } from '../../../utils/dateUtils';

const PRIMARY     = '#1b5e20';
const PRIMARY_MID = '#2e7d32';
const PRIMARY_LT  = '#e8f5e9';

const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtV = (n) => `₹${(parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

// Group flat rows by SaleReturnId into header + items
const groupRows = (rows) => {
    const map = {};
    const order = [];
    rows.forEach(r => {
        const key = r.SaleReturnId;
        if (!map[key]) {
            map[key] = {
                SaleReturnId:     key,
                SaleDate:         r.SaleDate,
                BillNumber:       r.BillNumber,
                CustomerName:     r.CustomerName,
                GSTNumber:        r.GSTNumber,
                OriginalBillNo:   r.OriginalBillNo,
                OriginalBillDate: r.OriginalBillDate,
                ReturnTotal:      parseFloat(r.ReturnTotal)      || 0,
                ReturnTaxable:    parseFloat(r.ReturnTaxable)    || 0,
                CGSTAmount:       parseFloat(r.CGSTAmount)       || 0,
                SGSTAmount:       parseFloat(r.SGSTAmount)       || 0,
                IGSTAmount:       parseFloat(r.IGSTAmount)       || 0,
                ReturnGrandTotal: parseFloat(r.ReturnGrandTotal) || 0,
                items: [],
            };
            order.push(key);
        }
        if (r.ItemId) {
            map[key].items.push({
                ItemId:               r.ItemId,
                ItemName:             r.ItemName,
                HSNCode:              r.HSNCode,
                GSTRate:              r.GSTRate,
                ReturnQty:            parseFloat(r.ReturnQty)            || 0,
                Rate:                 parseFloat(r.Rate)                 || 0,
                Amount:               parseFloat(r.Amount)               || 0,
                DiscountAmount:       parseFloat(r.DiscountAmount)       || 0,
                TaxableAmount:        parseFloat(r.TaxableAmount)        || 0,
                ItemCGST:             parseFloat(r.ItemCGST)             || 0,
                ItemSGST:             parseFloat(r.ItemSGST)             || 0,
                ItemIGST:             parseFloat(r.ItemIGST)             || 0,
                ItemTotal:            parseFloat(r.ItemTotal)            || 0,
                ReturnStockQuantity:  parseFloat(r.ReturnStockQuantity)  || 0,
                SaledQuantity:        parseFloat(r.SaledQuantity)        || 0,
            });
        }
    });
    return order.map(k => map[k]);
};

const SaleReturnAnalysisReport = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const { logAudit } = useAuditLog();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]     = useState(fmt2Date(today));
    const [grouped, setGrouped]   = useState([]);
    const [loading, setLoading]   = useState(false);
    const [error, setError]       = useState('');
    const [fetched, setFetched]   = useState(false);
    const [search, setSearch]     = useState('');
    const [expandedId, setExpandedId] = useState(null);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
      if (new Date(fromDate) > new Date(toDate)) {
        setData([]);
        setFetched(false);
        setError('From Date cannot be greater than To Date');
        return;
      }
      fetchReport();
    }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'SalesAnalysisReportApi',
                `/SaleReturnAnalysis/${fromDate}/${toDate}/${companyId}/${branchId}`,
                token
            );
            const rows = res?.response_data || res || [];
            const flat = Array.isArray(rows) ? rows : [];
            const result = groupRows(flat);
            if (result.length === 1) setExpandedId(result[0].SaleReturnId);
            setGrouped(result);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch sale return analysis');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        if (!search.trim()) return grouped;
        const q = search.toLowerCase();
        return grouped.filter(g =>
            (g.BillNumber    || '').toLowerCase().includes(q) ||
            (g.CustomerName  || '').toLowerCase().includes(q) ||
            (g.GSTNumber     || '').toLowerCase().includes(q) ||
            (g.OriginalBillNo|| '').toLowerCase().includes(q) ||
            g.items.some(r => (r.ItemName || '').toLowerCase().includes(q))
        );
    }, [grouped, search]);

    const totals = useMemo(() => filtered.reduce((acc, g) => ({
        ReturnGrandTotal: acc.ReturnGrandTotal + g.ReturnGrandTotal,
        ReturnTaxable:    acc.ReturnTaxable    + g.ReturnTaxable,
        CGSTAmount:       acc.CGSTAmount       + g.CGSTAmount,
        SGSTAmount:       acc.SGSTAmount       + g.SGSTAmount,
        IGSTAmount:       acc.IGSTAmount       + g.IGSTAmount,
    }), { ReturnGrandTotal: 0, ReturnTaxable: 0, CGSTAmount: 0, SGSTAmount: 0, IGSTAmount: 0 }), [filtered]);

    const handleExport = async () => {
        logAudit(AUDIT_ACTION.EXPORT, 'SaleReturnAnalysis', 0);
        if (!filtered.length) return;
        const headers = ['Return Bill', 'Return Date', 'Customer', 'GST No', 'Orig. Bill', 'Orig. Date',
            'Taxable', 'CGST', 'SGST', 'IGST', 'Grand Total',
            'Item', 'HSN', 'GST%', 'Ret Qty', 'Rate', 'Amount', 'Discount', 'Taxable', 'CGST', 'SGST', 'IGST', 'Item Total'];
        const rows = [];
        filtered.forEach(g => {
            g.items.forEach((r, idx) => {
                rows.push([
                    idx === 0 ? g.BillNumber : '',
                    idx === 0 ? fmtDate(g.SaleDate) : '',
                    idx === 0 ? g.CustomerName : '',
                    idx === 0 ? (g.GSTNumber || '-') : '',
                    idx === 0 ? (g.OriginalBillNo || '-') : '',
                    idx === 0 ? fmtDate(g.OriginalBillDate) : '',
                    idx === 0 ? g.ReturnTaxable : '',
                    idx === 0 ? g.CGSTAmount : '',
                    idx === 0 ? g.SGSTAmount : '',
                    idx === 0 ? g.IGSTAmount : '',
                    idx === 0 ? g.ReturnGrandTotal : '',
                    r.ItemName, r.HSNCode, r.GSTRate,
                    r.ReturnQty, r.Rate, r.Amount, r.DiscountAmount,
                    r.TaxableAmount, r.ItemCGST, r.ItemSGST, r.ItemIGST, r.ItemTotal,
                ]);
            });
        });

        const totalsRow = ['','','','','','Grand Total',
            totals.ReturnTaxable.toFixed(2), totals.CGSTAmount.toFixed(2), totals.SGSTAmount.toFixed(2), totals.IGSTAmount.toFixed(2), totals.ReturnGrandTotal.toFixed(2),
            '','','','','','','','','','','',''];

        await exportStyledExcel({
            reportTitle: 'SALE RETURN ANALYSIS REPORT',
            sheetName: 'Sale Return Analysis',
            fileName: `SaleReturnAnalysis_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow,
            columnWidths: [14, 12, 22, 16, 14, 12, 14, 12, 12, 12, 14, 24, 10, 8, 10, 10, 12, 12, 12, 10, 10, 10, 12],
        });
    };

    const thBase  = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #388e3c', color: '#fff', background: PRIMARY_MID };
    const thSub   = { padding: '5px 8px', fontWeight: 700, fontSize: 10, whiteSpace: 'nowrap', color: PRIMARY };
    const tdBase  = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };
    const tdSub   = { padding: '4px 8px', fontSize: 10, whiteSpace: 'nowrap', borderBottom: '1px solid #e2e8f0' };

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading sale return analysis..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Sales Return Analysis
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Sales Analysis Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    <input placeholder="Search bill / customer / item..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 200, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && filtered.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Returns',      value: filtered.length,                  color: PRIMARY },
                            { label: 'Taxable',      value: fmtV(totals.ReturnTaxable),       color: '#374151' },
                            { label: 'CGST',         value: fmtV(totals.CGSTAmount),          color: '#0277bd' },
                            { label: 'SGST',         value: fmtV(totals.SGSTAmount),          color: '#0277bd' },
                            { label: 'IGST',         value: fmtV(totals.IGSTAmount),          color: '#7c3aed' },
                            { label: 'Grand Total',  value: fmtV(totals.ReturnGrandTotal),    color: '#dc2626' },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 90 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} return bills
                        {search && ` (filtered from ${grouped.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 900, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, width: 32 }}></th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 110 }}>Return Bill</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 90 }}>Date</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 160 }}>Customer</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 120 }}>GST No</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 110 }}>Orig. Bill</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 90 }}>Orig. Date</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Taxable</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>CGST</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>SGST</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>IGST</th>
                                <th style={{ ...thBase, textAlign: 'right', background: '#b71c1c' }}>Grand Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={12} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((g, i) => {
                                const isOpen = expandedId === g.SaleReturnId;
                                return (
                                    <>
                                        {/* Master row */}
                                        <tr key={g.SaleReturnId}
                                            onClick={() => setExpandedId(isOpen ? null : g.SaleReturnId)}
                                            style={{ background: isOpen ? '#fce4ec' : (i % 2 === 0 ? '#fff' : PRIMARY_LT), cursor: 'pointer', borderBottom: '1px solid #e2e8f0', transition: 'background 0.15s' }}>
                                            <td style={{ ...tdBase, textAlign: 'center', color: PRIMARY }}>
                                                {isOpen ? <KeyboardArrowUp sx={{ fontSize: 16 }} /> : <KeyboardArrowDown sx={{ fontSize: 16 }} />}
                                            </td>
                                            <td style={{ ...tdBase, fontWeight: 700, color: '#dc2626' }}>{g.BillNumber || '-'}</td>
                                            <td style={{ ...tdBase, color: '#374151' }}>{fmtDate(g.SaleDate)}</td>
                                            <td style={{ ...tdBase, color: '#1e293b' }}>{g.CustomerName || '-'}</td>
                                            <td style={{ ...tdBase, color: '#64748b', fontSize: 10 }}>{g.GSTNumber || '-'}</td>
                                            <td style={{ ...tdBase, color: PRIMARY, fontWeight: 600 }}>{g.OriginalBillNo || '-'}</td>
                                            <td style={{ ...tdBase, color: '#374151' }}>{fmtDate(g.OriginalBillDate)}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#374151' }}>{fmtV(g.ReturnTaxable)}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd' }}>{g.CGSTAmount ? fmtV(g.CGSTAmount) : '-'}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd' }}>{g.SGSTAmount ? fmtV(g.SGSTAmount) : '-'}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', color: '#7c3aed' }}>{g.IGSTAmount ? fmtV(g.IGSTAmount) : '-'}</td>
                                            <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmtV(g.ReturnGrandTotal)}</td>
                                        </tr>

                                        {/* Expanded items */}
                                        {isOpen && (
                                            <tr key={`items-${g.SaleReturnId}`}>
                                                <td colSpan={12} style={{ padding: 0, background: '#fff8f8', borderBottom: `2px solid #dc2626` }}>
                                                    <div style={{ padding: '8px 12px 8px 44px' }}>
                                                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10 }}>
                                                            <thead>
                                                                <tr style={{ background: PRIMARY_LT }}>
                                                                    <th style={{ ...thSub, textAlign: 'left', minWidth: 180 }}>Item</th>
                                                                    <th style={{ ...thSub, textAlign: 'left' }}>HSN</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>GST%</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>Ret Qty</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>Rate</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>Amount</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>Discount</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>Taxable</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>CGST</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>SGST</th>
                                                                    <th style={{ ...thSub, textAlign: 'right' }}>IGST</th>
                                                                    <th style={{ ...thSub, textAlign: 'right', color: '#dc2626' }}>Item Total</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {g.items.map((r, idx) => (
                                                                    <tr key={idx} style={{ background: idx % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #e2e8f0' }}>
                                                                        <td style={{ ...tdSub, fontWeight: 600, color: PRIMARY }}>{r.ItemName || '-'}</td>
                                                                        <td style={{ ...tdSub, color: '#64748b' }}>{r.HSNCode || '-'}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#f59e0b' }}>{r.GSTRate ? `${r.GSTRate}%` : '-'}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#dc2626', fontWeight: 600 }}>{fmtQ(r.ReturnQty)}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#374151' }}>{fmtV(r.Rate)}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#374151' }}>{fmtV(r.Amount)}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#f59e0b' }}>{r.DiscountAmount ? fmtV(r.DiscountAmount) : '-'}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#374151' }}>{fmtV(r.TaxableAmount)}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#0277bd' }}>{r.ItemCGST ? fmtV(r.ItemCGST) : '-'}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#0277bd' }}>{r.ItemSGST ? fmtV(r.ItemSGST) : '-'}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', color: '#7c3aed' }}>{r.ItemIGST ? fmtV(r.ItemIGST) : '-'}</td>
                                                                        <td style={{ ...tdSub, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmtV(r.ItemTotal)}</td>
                                                                    </tr>
                                                                ))}
                                                                {/* Item subtotal */}
                                                                <tr style={{ background: PRIMARY_LT }}>
                                                                    <td colSpan={3} style={{ ...tdSub, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Subtotal</td>
                                                                    <td style={{ ...tdSub, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmtQ(g.items.reduce((s, r) => s + r.ReturnQty, 0))}</td>
                                                                    <td colSpan={3} style={{ ...tdSub }} />
                                                                    <td style={{ ...tdSub, textAlign: 'right', fontWeight: 700 }}>{fmtV(g.ReturnTaxable)}</td>
                                                                    <td style={{ ...tdSub, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(g.CGSTAmount)}</td>
                                                                    <td style={{ ...tdSub, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(g.SGSTAmount)}</td>
                                                                    <td style={{ ...tdSub, textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>{fmtV(g.IGSTAmount)}</td>
                                                                    <td style={{ ...tdSub, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmtV(g.ReturnGrandTotal)}</td>
                                                                </tr>
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </>
                                );
                            })}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700, position: 'sticky', bottom: 0 }}>
                                    <td colSpan={7} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Grand Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700 }}>{fmtV(totals.ReturnTaxable)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totals.CGSTAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#0277bd' }}>{fmtV(totals.SGSTAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#7c3aed' }}>{fmtV(totals.IGSTAmount)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', fontWeight: 700, color: '#dc2626' }}>{fmtV(totals.ReturnGrandTotal)}</td>
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default SaleReturnAnalysisReport;
